import { Box, BoxProps } from '@mui/material';
import { GuestJourneyKpiCard } from 'api/reports-service/guest-journey-reports.types';
import KpiCard from '../kpi-card/kpi-card.component';
import LimitedDataCard from '../kpi-card/limited-data-card.component';

export type MovementSummaryCardsProps = {
  /** Stable test prefix (e.g. "StageMovement" or "OfferImpact"). */
  testIdPrefix: string;
  /**
   * Ordered list of metric keys to render. Order is an FE decision — the BE
   * response is keyed by metric name. Titles/values/trends ride on each card.
   */
  cardOrder: string[];
  /** Keyed cards response — Stage Movement or Offer Impact. */
  data?: Record<string, GuestJourneyKpiCard | undefined>;
  loading?: boolean;
  /**
   * Optional grid column override. Defaults to a 5-up layout (Stage Movement);
   * Offer Impact passes a 6-up layout for its 12 cards.
   */
  gridTemplateColumns?: BoxProps['gridTemplateColumns'];
  /**
   * Card ids that should render the shared "Limited data" indicator instead of a
   * value — the selected date range is too short for that metric's window. Offer
   * Impact supplies this for its post-redemption cards; Stage Movement omits it.
   */
  limitedCardIds?: readonly string[];
};

const DEFAULT_GRID_TEMPLATE_COLUMNS: BoxProps['gridTemplateColumns'] = {
  xs: '1fr',
  sm: 'repeat(2, 1fr)',
  md: 'repeat(3, 1fr)',
  lg: 'repeat(5, 1fr)',
};

const MovementSummaryCards = ({
  testIdPrefix,
  cardOrder,
  data,
  loading = false,
  gridTemplateColumns = DEFAULT_GRID_TEMPLATE_COLUMNS,
  limitedCardIds,
}: MovementSummaryCardsProps) => (
  <Box
    display="grid"
    gap={2}
    gridTemplateColumns={gridTemplateColumns}
    data-testid={`${testIdPrefix}.SummaryCards`}
  >
    {cardOrder.map(id =>
      limitedCardIds?.includes(id) ? (
        <LimitedDataCard
          key={id}
          title={data?.[id]?.title}
          data-testid={`${testIdPrefix}.SummaryCard.${id}`}
        />
      ) : (
        <KpiCard
          key={id}
          card={data?.[id]}
          loading={loading}
          data-testid={`${testIdPrefix}.SummaryCard.${id}`}
        />
      ),
    )}
  </Box>
);

export default MovementSummaryCards;
